import { useCallback, useEffect, useMemo, useState } from 'react';
import constate from '../../common/lib/constate';
import { v4 as uuidv4 } from 'uuid';
import { useHistory, useLocation } from 'react-router-dom';
import { useVideoProgressContext } from './VideoProgressContext';

const MIN_CHAPTER_STEP = 1000;

function useChapters() {
  const [chapters, setChapters] = useState([]);
  const [isChaptersEditor, setIsChaptersEditor] = useState(false);
  const [newChapterStart, setNewChapterStart] = useState(0);
  const [hasChanged, setHasChanged] = useState(false);
  const [activeChapter, setActiveChapter] = useState(null);
  const [seekToActive, setSeekToActive] = useState(false);

  const history = useHistory();
  const location = useLocation();

  const { updateVideoSeekToValue } = useVideoProgressContext();

  const openChaptersEditor = useCallback(
    (pushHistory = true) => {
      setIsChaptersEditor(true);
      if (pushHistory) {
        history.push({
          pathname: location.pathname,
          search: 'chapters=true',
        });
      }
    },
    [location, history]
  );

  const closeChaptersEditor = (popHistory = true) => {
    setIsChaptersEditor(false);
    setHasChanged(false);
    if (popHistory) {
      if (history.length > 2) {
        history.goBack();
      } else {
        // Handle cases when page with search parameter was loaded
        history.replace({
          pathname: location.pathname,
          search: '',
        });
      }
    }
  };

  const updateChapterStartFromEditor = value => {
    updateVideoSeekToValue({ value, playing: false });
    setTimeout(() => {
      updateVideoSeekToValue(null);
    }, 10);
  };

  const updateChapterStart = useCallback(
    value => {
      const playedChapters = chapters.filter(item => value * 1000 >= item.startTime);
      !seekToActive && setActiveChapter(playedChapters.length > 0 ? playedChapters[playedChapters.length - 1].id : null);
      setNewChapterStart(value);
    },
    [chapters, seekToActive]
  );

  const handleAddChapter = useCallback(
    title => {
      let startTime = newChapterStart * 1000;
      setChapters(prevState => {
        const insertIndex = structuredClone(prevState).filter(item => item.startTime < newChapterStart * 1000).length;
        // Define Timestamps of previous/next chapters
        const chaptersBefore = prevState.slice(0, insertIndex);
        const chaptersAfter = prevState.slice(insertIndex);

        const beforeMinValue = chaptersBefore.length > 0 ? chaptersBefore[chaptersBefore.length - 1].startTime + MIN_CHAPTER_STEP : 0;
        const afterMaxValue = chaptersAfter.length
          ? chaptersAfter.length === prevState.length
            ? -1
            : chaptersAfter[0].startTime - MIN_CHAPTER_STEP
          : -1;

        if (startTime < beforeMinValue && afterMaxValue > 0 && startTime > afterMaxValue) {
          updateChapterStartFromEditor(prevState[insertIndex].startTime + MIN_CHAPTER_STEP / 1000);
          return prevState;
        }
        if (startTime < beforeMinValue && (afterMaxValue < 0 || startTime < afterMaxValue)) {
          startTime = beforeMinValue;
        }

        if (afterMaxValue > 0 && startTime > afterMaxValue && startTime > beforeMinValue) {
          startTime = afterMaxValue;
        }

        const newChapter = { id: uuidv4(), title, startTime };

        return [...chaptersBefore, newChapter, ...chaptersAfter];
      });
      updateChapterStartFromEditor((startTime + MIN_CHAPTER_STEP) / 1000);
      setHasChanged(true);
    },
    [setChapters, newChapterStart]
  );

  const onUpdatedChapter = useCallback((data, id) => {
    const dataKeys = Object.keys(data);
    setChapters(prevState =>
      prevState.map(item => {
        if (item.id === id) {
          const hasChanged = dataKeys.some(key => item[key] !== data[key]);
          hasChanged && setHasChanged(true);
          return { ...item, ...data };
        }
        return item;
      })
    );
  }, []);

  const handleRemoveChapter = useCallback(
    id => {
      const chapterIndex = chapters.findIndex(item => item.id === id);
      setChapters(prevState =>
        prevState
          .map((item, index, array) => {
            if (index === chapterIndex) {
              return null;
            } else if (index === chapterIndex + 1) {
              return { ...item, startTime: array[chapterIndex].startTime };
            }
            return item;
          })
          .filter(item => item !== null)
      );
      setHasChanged(true);
    },
    [chapters]
  );

  const onChooseChapter = id => {
    setActiveChapter(id);
    setSeekToActive(true);
    updateChapterStartFromEditor(chapters.find(item => item.id === id).startTime / 1000);
  };

  const updateInitialChapters = (chapters = []) => {
    setChapters(chapters);
  };

  useEffect(() => {
    seekToActive &&
      setTimeout(() => {
        setSeekToActive(false);
      }, 100);
  }, [seekToActive]);

  return useMemo(
    () => ({
      newChapterStart,
      isChaptersEditor,
      chapters,
      activeChapter,
      hasChanged,
      updateChapterStart,
      openChaptersEditor,
      closeChaptersEditor,
      updateChapterStartFromEditor,
      handleAddChapter,
      onUpdatedChapter,
      handleRemoveChapter,
      onChooseChapter,
      updateInitialChapters,
    }),
    [
      newChapterStart,
      isChaptersEditor,
      chapters,
      activeChapter,
      hasChanged,
      updateChapterStart,
      openChaptersEditor,
      closeChaptersEditor,
      updateChapterStartFromEditor,
      handleAddChapter,
      onUpdatedChapter,
      handleRemoveChapter,
      onChooseChapter,
      updateInitialChapters,
    ]
  );
}

export const useChaptersContext = constate(useChapters);

const ChaptersContext = {
  Provider: useChaptersContext.Provider,
};

export default ChaptersContext;
